import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ClientSidebar from '../components/Dashboard/ClientSidebar';
import { userAPI, getFullUrl } from '../services/api';

const CoachesList = () => {
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        setLoading(true);
        const response = await userAPI.getMyCoaches();
        const data = response.data;
        
        setCoaches(data);

        // Redirect logic: if exactly one approved coach exists and no other pending ones
        // OR as per user request: "если юзер привязан только к одному кочучу... переадресовывает"
        // "Если пользователь еще ждет подтверждения... показываем на /user/coaches ему информацию"
        if (data.length === 1 && data[0].isApproved) {
          navigate(`/user/video/${data[0].id}`);
        }
      } catch (err) {
        console.error('Error fetching coaches:', err);
        setError('Failed to load coaches. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCoaches();
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--bg-main)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[var(--tangerine)]"></div>
      </div>
    );
  }

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] group/design-root overflow-x-hidden font-display text-[var(--text-primary)]">
      <div className="layout-container flex h-full grow flex-col">
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 flex flex-1 justify-center py-5 w-full">
          <div className="layout-content-container flex flex-col lg:flex-row w-full max-w-[1200px] flex-1 gap-8">
            
            <main className="flex-1 flex flex-col">
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] tracking-tight mb-2">
                  Your Coaches
                </h1>
                <p className="text-[var(--text-secondary)] text-sm md:text-base">
                  {coaches.length > 0 
                    ? "Connect with your assigned coaches to achieve your goals."
                    : "You don't have any assigned coaches yet."}
                </p>
              </div>

              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
                  <span className="block sm:inline">{error}</span>
                </div>
              )}

              {/* Coaches Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {coaches.map((coach) => (
                  <div key={coach.id} className="bg-[var(--surface-white)] rounded-[2.5rem] shadow-xl shadow-black/5 border border-[var(--border-light)] overflow-hidden transition-all hover:border-[var(--tangerine)]/30 group">
                    <div className="p-8">
                      <div className="flex items-center mb-6">
                        <div className="w-24 h-24 rounded-full overflow-hidden bg-[var(--off-white)] border-4 border-white shadow-lg shrink-0 ring-4 ring-[var(--tangerine)]/10">
                          {coach.avatarUrl ? (
                             <img
                                src={getFullUrl(coach.avatarUrl)}
                                alt={coach.name}
                                className="w-full h-full object-cover"
                              />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-500">
                               <span className="material-symbols-outlined text-4xl">person</span>
                            </div>
                          )}
                        </div>
                        <div className="ml-6 flex-1 min-w-0">
                          <h3 className="text-xl font-black text-[var(--text-primary)] truncate mb-1">{coach.name}</h3>
                          <div className="inline-block px-3 py-1 bg-[var(--tangerine)]/10 border-[var(--tangerine)]/20">
                             <p className="text-[var(--tangerine)] font-bold text-[10px] uppercase tracking-widest">{coach.specialization || "Expert Coach"}</p>
                          </div>
                        </div>
                      </div>

                      <p className="text-[var(--text-secondary)] text-sm leading-relaxed mb-8 line-clamp-3 italic">
                        {coach.description ? `"${coach.description}"` : "Dedicated to helping you achieve your full potential through personalized AI-powered coaching."}
                      </p>

                      <div className="flex gap-3">
                        {!coach.isApproved ? (
                          <div className="flex-1 bg-gray-100 text-gray-400 font-bold py-4 px-4 rounded-2xl text-center text-sm border border-dashed border-gray-300">
                            Waiting for Approval
                          </div>
                        ) : (
                          <>
                            <Link
                              to={`/user/video/${coach.id}`}
                              className="flex-1 bg-[var(--tangerine)] hover:bg-[#d97700] text-white font-bold shadow-lg shadow-orange-500/30 transition-all hover:-translate-y-0.5 py-4 px-4 rounded-2xl text-center text-sm"
                            >
                              Start Session
                            </Link>
                            <Link
                              to={`/user/chat/${coach.id}`}
                              className="w-14 h-14 bg-[var(--surface-white)] border border-[var(--border-light)] hover:border-[var(--tangerine)] hover:text-[var(--tangerine)] text-gray-400 rounded-2xl flex items-center justify-center transition-all shadow-sm flex-shrink-0"
                            >
                              <span className="material-symbols-outlined filled-icon">chat</span>
                            </Link>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </main>

            <ClientSidebar selectedCoach={null} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachesList;
